import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { CheckCircle2, Plus } from 'lucide-react';

import RoleDialog from './RoleDialog';
import WorkExperienceDetails from './WorkExperienceDetails/WorkExperienceDetails';
import useCareerEntries, { useEntryDialog } from '../useCareerEntries';

import useAuth from 'hooks/useAuth';
import { sortWorkExperienceByStartDateDesc } from 'components/Shared/WorkExperienceDisplay/WorkExperienceDisplay';

import styles from '../CareerEntries.module.scss';
import hocStyles from '../../hoc/hoc.module.scss';
import Loader from 'components/Loader/Loader';

const {
  panel,
  dashedPanel,
  headerContainer,
  header,
  count,
  hiddenBadge,
  placeholder: placeholderStyle,
  addLink,
  addBtn,
  editWrp,
  loader,
  tick,
} = hocStyles;

// Roles shown in full, then as single rows, before "Show all" reveals the rest.
const FULL_ROLES = 3;
const COMPACT_ROLES = 5;

const META = {
  lastUpdated: 'workexperience',
  eventName: 'freelancer-updates-work-experience',
};

const roleName = (role) =>
  [role.job_title, role.company_name].filter(Boolean).join(' at ') || 'role';

const WorkExperience = ({ user, isOwn, userData, isEmpty, placeholder }) => {
  const { lastUpdated, updating, updated } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const { dialog, open, close } = useEntryDialog();
  const { visibleEntries, saving, saveEntry, deleteEntry } = useCareerEntries({
    user,
    entries: userData,
    field: 'workexperience',
  });

  // An edited role moves to its new place by start date after saving.
  const roles = useMemo(
    () => sortWorkExperienceByStartDateDesc(visibleEntries),
    [visibleEntries]
  );

  const instanceIsUpdating = lastUpdated === 'workexperience';

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !dialog.open && (
      <CheckCircle2 className={tick} size={20} aria-label="Saved" />
    );

  if (!isOwn && isEmpty) {
    return null;
  }

  const showOwnerEmpty = isOwn && roles.length === 0;
  const total = roles.length;
  const fullRoles = showAll ? roles : roles.slice(0, FULL_ROLES);
  const compactRoles = showAll
    ? []
    : roles.slice(FULL_ROLES, FULL_ROLES + COMPACT_ROLES);

  const renderRoles = (items, compact) =>
    items.map((role, index) => (
      <WorkExperienceDetails
        key={role.id ? `weDetails${role.id}` : `weDetails${index}`}
        workExperience={role}
        isOwn={isOwn}
        user={user}
        compact={compact}
        expanded={showAll}
        onEdit={() => open(role)}
      />
    ));

  return (
    <section
      id="portfolio-work-experience"
      className={showOwnerEmpty ? dashedPanel : panel}
    >
      <div className={headerContainer}>
        <h2 className={header}>
          Work experience
          {total > FULL_ROLES && <span className={count}>· {total} roles</span>}
        </h2>
        {showOwnerEmpty ? (
          <span className={hiddenBadge}>Hidden until added</span>
        ) : (
          user &&
          isOwn && (
            <div className={editWrp}>
              {successTick}
              {instanceIsUpdating && updating && !dialog.open && (
                <Loader loading className={loader} />
              )}
              <button
                id="add-work-experience"
                type="button"
                className={addBtn}
                onClick={() => open()}
              >
                <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
                Add role
              </button>
            </div>
          )
        )}
      </div>
      {showOwnerEmpty ? (
        <p className={placeholderStyle}>
          {placeholder}{' '}
          <button type="button" className={addLink} onClick={() => open()}>
            Add a role
          </button>
        </p>
      ) : (
        <div className={styles.list}>
          {renderRoles(fullRoles, false)}
          {compactRoles.length > 0 && (
            <div className={`${styles.compactList} ${styles.divided}`}>
              {renderRoles(compactRoles, true)}
            </div>
          )}
          {total > FULL_ROLES && (
            <button
              type="button"
              className={`${styles.showAll} ${styles.showAllIndented}`}
              onClick={() => setShowAll(!showAll)}
              aria-expanded={showAll}
            >
              {showAll
                ? 'Show fewer roles'
                : `Show all ${total} roles with details`}
            </button>
          )}
        </div>
      )}

      {isOwn && dialog.key > 0 && (
        <RoleDialog
          key={dialog.key}
          show={dialog.open}
          role={dialog.entry}
          saving={saving}
          onSave={(role) => saveEntry(dialog.entry, role, META)}
          onDelete={() => {
            close();
            deleteEntry(
              dialog.entry,
              `Role deleted: ${roleName(dialog.entry)}`,
              META
            );
          }}
          onClose={close}
        />
      )}
    </section>
  );
};

WorkExperience.propTypes = {
  user: PropTypes.object,
  isOwn: PropTypes.bool,
  userData: PropTypes.array,
  isEmpty: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default WorkExperience;
